import React from 'react';
import HeaderFooterLayout from '../components/HeaderFooterLayout';
import { Gavel } from 'lucide-react';

const Section = ({ title, children }) => (
  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-zinc-700 pb-2">
      {title}
    </h2>
    <div className="text-gray-600 dark:text-gray-400 space-y-3 leading-relaxed">
      {children}
    </div>
  </section>
);

export default function TermsOfUse() {
  return (
    <HeaderFooterLayout>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl p-8 md:p-12">
          <div className="text-center mb-12">
            <Gavel className="mx-auto h-12 w-12 text-neutral-700 dark:text-gray-300 mb-4" />
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Terms of Use</h1>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Last Updated: November 2025</p>
          </div>

          <div className="space-y-10">
            <Section title="1. Acceptance of Terms">
              <p>
                These Terms govern your use of <strong>LumoChart</strong>, <strong>LumoDx</strong>, and other services operated
                by <strong>Lumosye Health Services, LLC</strong>. By using our software, you agree to comply with these Terms
                and our Privacy Policy.
              </p>
            </Section>

            <Section title="2. Description of Services">
              <p>
                Lumosye Health provides tools to enhance clinical documentation and decision-making. Our apps are not certified
                EHRs and should not replace professional judgment or institutional policies.
              </p>
            </Section>

            <Section title="3. Eligibility and Responsibilities">
              <ul className="list-disc pl-5 space-y-2">
                <li>Users must be licensed or authorized healthcare professionals.</li>
                <li>You are responsible for data accuracy and compliance with laws.</li>
                <li>Maintain confidentiality of login credentials.</li>
                <li>Do not misuse the platform or violate HIPAA standards.</li>
              </ul>
            </Section>

            <Section title="4. Data Management">
              <p>
                Patient data entered into LumoChart is <strong>deleted after 15 days</strong> to limit PHI retention.
                Users must export notes if longer storage is needed.
              </p>
            </Section>

            <Section title="5. Subscriptions">
              <p>
                Premium features such as <strong>LumoChart Pro</strong> are billed through Apple. Renewals follow Apple’s
                billing terms. Trials end automatically upon paid activation.
              </p>
            </Section>

            <Section title="6. Intellectual Property">
              <p>
                All software, branding, and materials are property of Lumosye Health Services, LLC. Redistribution or
                modification without written consent is prohibited.
              </p>
            </Section>

            <Section title="7. Disclaimer of Warranties">
              <p>
                Services are provided “as is.” We make no guarantees of uptime, accuracy, or suitability. Users are solely
                responsible for their clinical use.
              </p>
            </Section>

            <Section title="8. Limitation of Liability">
              <p>
                Lumosye Health is not liable for indirect or consequential damages including data loss or downtime-related
                impacts.
              </p>
            </Section>

            <Section title="9. Indemnification">
              <p>
                You agree to hold Lumosye Health harmless from claims arising from misuse or breach of these Terms.
              </p>
            </Section>

            <Section title="10. Governing Law">
              <p>These Terms are governed by Texas law, with exclusive venue in Harris County, TX.</p>
            </Section>

            <Section title="11. Modifications">
              <p>
                Lumosye Health may revise these Terms periodically. Continued use after updates indicates acceptance.
              </p>
            </Section>

            <Section title="12. Contact Us">
              <p>
                <strong>Lumosye Health Services, LLC</strong><br />
                📧{' '}
                <a
                  href="mailto:contact@lumosyehealth.com"
                  className="text-neutral-700 dark:text-gray-200 underline hover:text-neutral-900 dark:hover:text-white"
                >
                  contact@lumosyehealth.com
                </a>
              </p>
            </Section>
          </div>
        </div>
      </div>
    </HeaderFooterLayout>
  );
}
